import type { IPropertyBag } from '@deity-io/di-data-basics';
import { FlexLayout, Icon, Input, Modal } from '@deity-io/portal-ui';
import EnvironmentVariableContainer from 'components/Package/VariablesTab/EnvironmentVariableContainer';
import { ENTITY_PATH, ENTITY_PATH_ID } from 'contracts/constants';
import { useFormik } from 'formik';
import type { Dispatch, FC, SetStateAction } from 'react';
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { getParams, isValidFieldName } from 'utils';
import * as Yup from 'yup';

type CreateEditVariableModalProps = {
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  variable?: IPropertyBag;
  variableKey: string;
  onUpdate?: (key: string, value: IPropertyBag | undefined) => void;
};

const CreateEditVariableInputSchema = Yup.object().shape({
  name: Yup.string().min(2, 'Too short').max(50, 'Too long').required('This is a required field.')
});

const CreateEditVariableModal: FC<CreateEditVariableModalProps> = ({
  isOpen,
  setIsOpen,
  variableKey,
  variable,
  onUpdate
}) => {
  const { pathname } = useLocation();
  const params: { [key: string]: string } = getParams(pathname, [ENTITY_PATH_ID, ENTITY_PATH]);

  const keyField = '_key';
  const loading = false;
  const { values, handleSubmit, setFieldTouched, setFieldValue, errors, setValues } = useFormik({
    initialValues: { ...variable } || ({} as IPropertyBag),
    validateOnBlur: true,
    // validationSchema: CreateEditVariableInputSchema,
    validate: validationValues => {
      const validationErrors: IPropertyBag = {};

      if (values[keyField]) {
        if (!isValidFieldName.test(values[keyField])) {
          validationErrors.name = `Invalid variable name '${values[keyField]}'`;
        }
      }

      return validationErrors;
    },
    onSubmit: async v => {
      if (onUpdate) {
        onUpdate(variableKey, v);
      }

      setIsOpen(false);
    }
  });
  useEffect(() => {
    if (variable) {
      setValues(variable);
    }
  }, [variable]);

  const confirmText = () => {
    if (!variable) {
      return `${loading ? 'Creating' : 'Create'} variable`;
    }

    return `${loading ? 'Updating' : 'Update'} variable`;
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => setIsOpen(false)}
      closeIcon={<Icon icon="close" />}
      label={`${variable ? 'Edit' : 'New'} Variable`}
      title="Set your variable for each environment."
      confirmText={confirmText()}
      onConfirm={() => handleSubmit()}
    >
      <form onSubmit={handleSubmit}>
        <FlexLayout flexDirection="column">
          <Input
            required
            size="small"
            label="Variable"
            value={values[keyField] ? values[keyField] : variableKey}
            placeholder="Variable name"
            onBlur={() => setFieldTouched(keyField, true)}
            onChange={e => setFieldValue(keyField, e.target.value)}
            error={errors && 'name' in errors}
            helperText={errors.name}
            sx={{ marginY: 2 }}
          />

          <EnvironmentVariableContainer
            values={values}
            variableKey={variableKey}
            setFieldValue={setFieldValue}
            setFieldTouched={setFieldTouched}
            errors={errors}
          />
        </FlexLayout>
      </form>
    </Modal>
  );
};

export default CreateEditVariableModal;
